import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FlaskConical, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const initialForm = {
  fullName: '',
  email: '',
  company: '',
  phone: '',
  shippingAddress: '',
  message: '',
};

const RequestTesterModal = ({ isOpen, onClose, productName = '' }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.company.trim()) e.company = 'Required';
    if (!form.shippingAddress.trim()) e.shippingAddress = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/request-tester`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, productName }),
      });
      if (!res.ok) throw new Error('Submit failed');
      setIsSuccess(true);
    } catch (err) {
      console.error('Request tester error:', err);
      setErrors(prev => ({ ...prev, submit: "We couldn't submit your tester request. Please try again, or email samples@porticosystems.com." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setForm(initialForm);
      setErrors({});
      setIsSuccess(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="request-tester-modal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-stone-100 transition-colors"
              aria-label="Close"
              data-testid="request-tester-close-btn"
            >
              <X className="w-4 h-4 text-stone-400" />
            </button>

            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <FlaskConical className="w-4.5 h-4.5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-semibold text-stone-900">Request a Product Tester</h2>
              </div>
            </div>

            <p className="text-sm text-stone-500 leading-relaxed">
              Get a sample of this product to evaluate for your space. Fill out a few quick details and we'll take care of the rest.
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <Clock className="w-3 h-3 text-stone-400" />
              <span className="text-xs text-stone-400">Takes less than 30 seconds</span>
            </div>
          </div>

          {isSuccess ? (
            <div className="px-6 pb-8 pt-4 text-center" data-testid="request-tester-success">
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                <FlaskConical className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-base font-semibold text-stone-900 mb-1.5">Tester Request Received</h3>
              <p className="text-sm text-stone-500 mb-5">
                We'll ship your {productName || 'product'} tester shortly. Our team may follow up with any questions.
              </p>
              <Button size="sm" onClick={handleClose} data-testid="request-tester-done-btn">Done</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-3.5" data-testid="request-tester-form">
              {/* Product (read-only) */}
              {productName && (
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Product</label>
                  <div className="px-3 py-2 text-sm bg-stone-50 border border-stone-100 rounded-lg text-stone-700" data-testid="request-tester-product">
                    {productName}
                  </div>
                </div>
              )}

              {/* Name + Company */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Full Name" required error={errors.fullName}>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={e => update('fullName', e.target.value)}
                    className={inputCls(errors.fullName)}
                    placeholder="Jane Doe"
                    data-testid="request-tester-name"
                  />
                </Field>
                <Field label="Company Name" required error={errors.company}>
                  <input
                    type="text"
                    value={form.company}
                    onChange={e => update('company', e.target.value)}
                    className={inputCls(errors.company)}
                    placeholder="Acme Corp"
                    data-testid="request-tester-company"
                  />
                </Field>
              </div>

              {/* Email + Phone */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Work Email" required error={errors.email}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => update('email', e.target.value)}
                    className={inputCls(errors.email)}
                    placeholder="jane@company.com"
                    data-testid="request-tester-email"
                  />
                </Field>
                <Field label="Phone" error={errors.phone}>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => update('phone', e.target.value)}
                    className={inputCls()}
                    placeholder="(555) 123-4567"
                    data-testid="request-tester-phone"
                  />
                </Field>
              </div>

              {/* Shipping Address */}
              <Field label="Shipping Address" required error={errors.shippingAddress}>
                <input
                  type="text"
                  value={form.shippingAddress}
                  onChange={e => update('shippingAddress', e.target.value)}
                  className={inputCls(errors.shippingAddress)}
                  placeholder="123 Main St, Suite 100, City, State ZIP"
                  data-testid="request-tester-address"
                />
              </Field>

              {/* Message */}
              <Field label="Message">
                <textarea
                  value={form.message}
                  onChange={e => update('message', e.target.value)}
                  className={`${inputCls()} min-h-[64px] resize-none`}
                  placeholder="Tell us a bit about your project or how you plan to use this product."
                  rows={2}
                  data-testid="request-tester-message"
                />
              </Field>

              {/* Submit */}
              {errors.submit && (
                <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg" data-testid="request-tester-submit-error">{errors.submit}</p>
              )}
              <Button
                type="submit"
                className="w-full font-semibold"
                disabled={isSubmitting}
                data-testid="request-tester-submit"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  'Request Tester'
                )}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-stone-600 mb-1">
      {label}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-[11px] text-red-500 mt-0.5">{error}</p>}
  </div>
);

const inputCls = (error) =>
  `w-full px-3 py-2 text-sm border rounded-lg transition-colors outline-none
   ${error
     ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-200'
     : 'border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20'
   } bg-white placeholder:text-stone-400`;

export default RequestTesterModal;
